/**
 * Render configuration — scoped domain model
 *
 * Render method + its associated settings (AO, shadows, bloom, env map,
 * etc.) are split into two independent **scopes**:
 *
 *   - `viewport` — applies while the user is in a creation workspace
 *     (Edit, Object, Pose, Sketch, Sculpt, Reference).
 *   - `render`   — applies while the user is in an output workspace
 *     (Inking, Render).
 *
 * The active scope is a function of `workspaceMode`.  Consumers read the
 * active config via `useActiveRenderConfig` (React) or
 * `selectActiveRenderConfig` (imperative store access).
 *
 * Viewport quality (`maxViewportDpr`, `shadowMapResolution`) is **not**
 * scoped — it's a global property of the live canvas so draft ↔ render
 * mode switches don't trigger a canvas buffer reallocation / shadow-map
 * resize flash. See `ViewportQualitySettings` below. Export paths may
 * push these higher via `ExportQualityOverrides` since export renders
 * offscreen and is not bound to viewport perf.
 */

import { useShallow } from "zustand/react/shallow";
import { useStore } from "./store.ts";
import type { AppState, ViewMode, WorkspaceMode } from "./storeTypes.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RenderScope = "viewport" | "render";

/**
 * The complete render-method configuration for a single scope. Every field
 * is independent between the two scopes so e.g. AO can be enabled in the
 * Render scope while staying off in the Viewport scope.
 */
export interface RenderConfig {
  viewMode: ViewMode;

  // Overlays
  showWireframe: boolean;
  wireframeXray: boolean;

  // Matcap
  matcapPreset: string;

  // Lighting / post
  ambientColor: string;
  ambientIntensity: number;
  aoEnabled: boolean;
  aoIntensity: number;
  bloomEnabled: boolean;
  bloomIntensity: number;
  bloomThreshold: number;
  shadowsEnabled: boolean;
  shadowSoftness: number;
  envMapEnabled: boolean;
  envMapPreset: string;
  envMapIntensity: number;
  envMapBackground: boolean;

  // Viewport background
  viewportBgPreset: string;
}

/**
 * Global live-canvas quality. Shared by every workspace so switching
 * between draft (viewport) and output (render/inking) scopes doesn't
 * flash — no DPR change, no shadow-map resize. Lives under
 * `AppState.viewportQuality` and is edited from Settings → Viewport →
 * Quality.
 */
export interface ViewportQualitySettings {
  maxViewportDpr: number; // 1 / 1.5 / 2
  shadowMapResolution: number; // 256 / 512 / 1024 / 2048
}

/**
 * Per-field overrides applied by the offscreen export path (download,
 * inking PNG, etc.). A `null` field means "inherit the live viewport
 * value." Since export renders offscreen it can push higher quality
 * without affecting interactive perf.
 *
 * Lives under `AppState.exportQualityOverrides`. Resolve with
 * `resolveExportQuality(viewportQuality, exportQualityOverrides)` in the
 * export path.
 */
export interface ExportQualityOverrides {
  maxViewportDpr: number | null;
  shadowMapResolution: number | null;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const SHARED_DEFAULTS = {
  showWireframe: false,
  wireframeXray: false,
  matcapPreset: "clay",
  ambientColor: "#ffffff",
  ambientIntensity: 0.08,
  aoEnabled: false,
  aoIntensity: 0.4,
  bloomEnabled: false,
  bloomIntensity: 0.5,
  bloomThreshold: 0.9,
  shadowsEnabled: false,
  shadowSoftness: 25,
  envMapEnabled: true,
  envMapPreset: "forest",
  envMapIntensity: 0.3,
  envMapBackground: false,
  viewportBgPreset: "default",
} as const;

export const DEFAULT_VIEWPORT_RENDER_CONFIG: RenderConfig = {
  viewMode: "matcap",
  ...SHARED_DEFAULTS,
};

export const DEFAULT_RENDER_RENDER_CONFIG: RenderConfig = {
  viewMode: "material",
  ...SHARED_DEFAULTS,
  ambientIntensity: 0,
};

export const DEFAULT_RENDER_CONFIGS: Record<RenderScope, RenderConfig> = {
  viewport: DEFAULT_VIEWPORT_RENDER_CONFIG,
  render: DEFAULT_RENDER_RENDER_CONFIG,
};

/**
 * Default live-canvas quality. A single DPR/shadow-map pair shared by
 * both draft and render modes — higher than the old draft defaults and
 * lower than the old render defaults, so interaction stays smooth while
 * final-output pushes higher quality via export overrides.
 */
export const DEFAULT_VIEWPORT_QUALITY: ViewportQualitySettings = {
  maxViewportDpr: 1.5,
  shadowMapResolution: 1024,
};

/**
 * Default export overrides. Push render output to 2× DPR and 2048 shadow
 * maps (matching the old per-scope render defaults) without affecting
 * live viewport cost.
 */
export const DEFAULT_EXPORT_QUALITY_OVERRIDES: ExportQualityOverrides = {
  maxViewportDpr: 2,
  shadowMapResolution: 2048,
};

// ---------------------------------------------------------------------------
// Scope mapping
// ---------------------------------------------------------------------------

const RENDER_WORKSPACE_SET: ReadonlySet<WorkspaceMode> = new Set([
  "render",
  "inking",
]);

export function renderScopeFor(mode: WorkspaceMode): RenderScope {
  return RENDER_WORKSPACE_SET.has(mode) ? "render" : "viewport";
}

// ---------------------------------------------------------------------------
// Method availability per scope
// ---------------------------------------------------------------------------

export const VIEW_MODES_BY_SCOPE: Record<RenderScope, readonly ViewMode[]> = {
  viewport: ["wireframe", "matcap", "material"] as const,
  render: ["wireframe", "matcap", "material"] as const,
};

export const RENDER_MODE_LABELS: Record<ViewMode, string> = {
  wireframe: "Wireframe",
  matcap: "Matcap",
  material: "Material",
};

// ---------------------------------------------------------------------------
// Compatibility — which view modes each setting affects
// ---------------------------------------------------------------------------

/**
 * Map from `RenderConfig` key to the view modes in which the setting has
 * a visible effect.  Settings not present in this map apply in every mode.
 *
 * Drives the "not visible in {viewMode} mode" warning badge shown next to
 * controls in `RenderMethodModal`. Controls stay interactive so users can
 * pre-configure them before switching method.
 */
export const SETTING_COMPATIBILITY: Partial<
  Record<keyof RenderConfig, readonly ViewMode[]>
> = {
  aoEnabled: ["material"],
  aoIntensity: ["material"],
  bloomEnabled: ["material"],
  bloomIntensity: ["material"],
  bloomThreshold: ["material"],
  shadowsEnabled: ["material"],
  shadowSoftness: ["material"],
  ambientColor: ["material"],
  ambientIntensity: ["material"],
  envMapEnabled: ["material"],
  envMapPreset: ["material"],
  envMapIntensity: ["material"],
  envMapBackground: ["material"],
  wireframeXray: ["wireframe"],
  matcapPreset: ["matcap"],
  showWireframe: ["matcap", "material"],
  // viewModeMode and viewportBgPreset are compatible with every mode.
};

export function settingCompatibleWith(
  key: keyof RenderConfig,
  mode: ViewMode,
): boolean {
  const modes = SETTING_COMPATIBILITY[key];
  return !modes || modes.includes(mode);
}

// ---------------------------------------------------------------------------
// Clamping — authoritative valid ranges for numeric settings
// ---------------------------------------------------------------------------

type NumericKey = {
  [K in keyof RenderConfig]: RenderConfig[K] extends number ? K : never;
}[keyof RenderConfig];

const NUMERIC_CLAMPS: Record<NumericKey, [number, number]> = {
  ambientIntensity: [0, 2],
  aoIntensity: [0, 3],
  bloomIntensity: [0, 3],
  bloomThreshold: [0, 2],
  shadowSoftness: [1, 100],
  envMapIntensity: [0, 3],
};

/** Valid ranges for `ViewportQualitySettings` fields. */
const VIEWPORT_QUALITY_CLAMPS: Record<
  keyof ViewportQualitySettings,
  [number, number]
> = {
  maxViewportDpr: [1, 2],
  shadowMapResolution: [256, 2048],
};

/** Clamp a viewport-quality field to its valid range. */
export function clampViewportQuality<K extends keyof ViewportQualitySettings>(
  key: K,
  value: ViewportQualitySettings[K],
): ViewportQualitySettings[K] {
  const range = VIEWPORT_QUALITY_CLAMPS[key];
  return Math.max(
    range[0],
    Math.min(range[1], value),
  ) as ViewportQualitySettings[K];
}

/**
 * Clamp an export-override value. `null` passes through unchanged
 * (means "inherit live viewport value").
 */
export function clampExportOverride<K extends keyof ExportQualityOverrides>(
  key: K,
  value: ExportQualityOverrides[K],
): ExportQualityOverrides[K] {
  if (value == null) return null as ExportQualityOverrides[K];
  const range = VIEWPORT_QUALITY_CLAMPS[key];
  return Math.max(
    range[0],
    Math.min(range[1], value),
  ) as ExportQualityOverrides[K];
}

/**
 * Resolve effective export quality by merging overrides on top of the
 * live viewport settings. `null` override fields inherit viewport.
 */
export function resolveExportQuality(
  viewport: ViewportQualitySettings,
  overrides: ExportQualityOverrides,
): ViewportQualitySettings {
  return {
    maxViewportDpr: overrides.maxViewportDpr ?? viewport.maxViewportDpr,
    shadowMapResolution:
      overrides.shadowMapResolution ?? viewport.shadowMapResolution,
  };
}

/**
 * Apply the domain-level valid range for the given setting. Non-numeric
 * and unclamped fields pass through unchanged.
 */
export function clampRenderSetting<K extends keyof RenderConfig>(
  key: K,
  value: RenderConfig[K],
): RenderConfig[K] {
  const range = NUMERIC_CLAMPS[key as NumericKey];
  if (range && typeof value === "number") {
    return Math.max(range[0], Math.min(range[1], value)) as RenderConfig[K];
  }
  return value;
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export function selectActiveRenderScope(s: AppState): RenderScope {
  return renderScopeFor(s.workspaceMode);
}

export function selectActiveRenderConfig(s: AppState): RenderConfig {
  return s.renderConfigs[selectActiveRenderScope(s)];
}

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

/**
 * Subscribe to a projection of the active scope's render config. Automatically
 * switches which scope it reads from when `workspaceMode` changes.
 */
export function useActiveRenderConfig<T>(selector: (c: RenderConfig) => T): T {
  return useStore((s) => selector(selectActiveRenderConfig(s)));
}

/**
 * Shallow-equal multi-field read from the active scope. Use when pulling
 * several fields at once so the component re-renders only when one of them
 * actually changes.
 */
export function useActiveRenderConfigShallow<T extends object>(
  selector: (c: RenderConfig) => T,
): T {
  return useStore(useShallow((s) => selector(selectActiveRenderConfig(s))));
}

export function useRenderConfig<T>(
  scope: RenderScope,
  selector: (c: RenderConfig) => T,
): T {
  return useStore((s) => selector(s.renderConfigs[scope]));
}

export function useActiveRenderScope(): RenderScope {
  return useStore(selectActiveRenderScope);
}

/**
 * Read a field of the live viewport quality settings. Shared across
 * every workspace — `useActiveRenderScope` changes do not affect this.
 */
export function useViewportQuality<T>(
  selector: (q: ViewportQualitySettings) => T,
): T {
  return useStore((s) => selector(s.viewportQuality));
}

/**
 * Imperative read of the effective export quality (viewport ∘ overrides).
 * Use this from the export/download path; the live canvas should read
 * `viewportQuality` directly.
 */
export function selectEffectiveExportQuality(
  s: AppState,
): ViewportQualitySettings {
  return resolveExportQuality(s.viewportQuality, s.exportQualityOverrides);
}
